import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Box,
  CircularProgress,
  Dialog,
  DialogContent,
  Grid,
} from '@mui/material';
import CodeIcon from '@mui/icons-material/Code';
import DescriptionOutlinedIcon from '@mui/icons-material/DescriptionOutlined';
import FormatBoldIcon from '@mui/icons-material/FormatBold';
import FormatItalicIcon from '@mui/icons-material/FormatItalic';
import FormatListBulletedIcon from '@mui/icons-material/FormatListBulleted';
import FormatListNumberedIcon from '@mui/icons-material/FormatListNumbered';
import FormatQuoteIcon from '@mui/icons-material/FormatQuote';
import FormatUnderlinedIcon from '@mui/icons-material/FormatUnderlined';
import ImageOutlinedIcon from '@mui/icons-material/ImageOutlined';
import InsertLinkIcon from '@mui/icons-material/InsertLink';
import PictureAsPdfOutlinedIcon from '@mui/icons-material/PictureAsPdfOutlined';
import TableChartOutlinedIcon from '@mui/icons-material/TableChartOutlined';
import ArchiveOutlinedIcon from '@mui/icons-material/ArchiveOutlined';
import api, { getApiBaseUrl } from '../../api/config';
import { ATTACHMENTS, LEADS, SERVICES, STATUSES, TASKS, TASK_COMMENTS, USERS } from '../../api/endpoints';
import { extractList } from '../../utils/response';
import { toast } from 'react-toastify';
import { useAuth } from '../../hooks/useAuth';
import {
  buildTaskRelationParams,
  formatTaskDeadlineForApi,
  formatTaskDeadlineForInput,
  leadLabel,
  PRIORITY_OPTIONS,
  serviceLabel,
  userLabel,
} from './taskApiHelpers';
import TaskDetailsHeader from './taskDetailsModal/TaskDetailsHeader';
import EditableTaskTextCard from './taskDetailsModal/EditableTaskTextCard';
import TaskActivityTabsCard from './taskDetailsModal/TaskActivityTabsCard';
import TaskSummaryPanel from './taskDetailsModal/TaskSummaryPanel';

const statusLabel = (s) => s.label ?? s.name ?? s.title ?? String(s.id);

const userOption = (user) => ({
  id: user?.id ?? user?.uuid,
  label: userLabel(user),
  raw: user,
});

const leadOption = (lead) => ({
  id: lead?.id,
  label: leadLabel(lead),
  raw: lead,
});

const serviceOption = (service) => ({
  id: service?.id,
  label: serviceLabel(service),
  raw: service,
});

const taskOption = (task) => ({
  id: task?.id,
  label: task?.title ?? String(task?.id ?? ''),
  raw: task,
});

const ensureOption = (list, option) => {
  if (!option?.id) return list;
  return list.some((item) => item.id === option.id) ? list : [...list, option];
};

const extractArray = (response) => {
  const list =
    response?.data?.list ??
    response?.data?.data?.list ??
    response?.list ??
    response?.data ??
    response;
  return Array.isArray(list) ? list : [];
};

const commentTimestamp = (comment) => {
  const raw = comment?.created_at ?? comment?.updated_at ?? comment?.date ?? '';
  if (!raw) return Number.NaN;

  const direct = Date.parse(String(raw));
  if (!Number.isNaN(direct)) return direct;

  const normalized = String(raw).trim().replace(' ', 'T');
  const fallback = Date.parse(normalized);
  return Number.isNaN(fallback) ? Number.NaN : fallback;
};

const sortCommentsOldestFirst = (items) =>
  items
    .map((item, index) => ({ item, index, time: commentTimestamp(item) }))
    .sort((a, b) => {
      const aValid = !Number.isNaN(a.time);
      const bValid = !Number.isNaN(b.time);
      if (aValid && bValid && a.time !== b.time) return a.time - b.time;
      if (aValid !== bValid) return aValid ? -1 : 1;
      return a.index - b.index;
    })
    .map(({ item }) => item);

const normalizeCommentAttachments = (comment) => {
  const directList =
    comment?.attachments ??
    comment?.comment_attachments ??
    comment?.task_attachments ??
    comment?.documents ??
    comment?.files ??
    comment?.media ??
    comment?.comment_files ??
    null;

  if (Array.isArray(directList) && directList.length > 0) {
    return directList;
  }

  if (comment?.attachment && typeof comment.attachment === 'object') {
    return [comment.attachment];
  }

  const idList =
    comment?.attachment_ids ??
    comment?.attachments_ids ??
    comment?.document_ids ??
    comment?.file_ids ??
    [];

  if (Array.isArray(idList) && idList.length > 0) {
    return idList.map((id, index) => ({
      id,
      attachment_id: id,
      title: `Attachment ${index + 1}`,
      document_name: `Attachment ${index + 1}`,
    }));
  }

  return [];
};

const normalizeSingleComment = (comment) => ({
  ...comment,
  attachments: normalizeCommentAttachments(comment),
});

const normalizeCommentList = (taskPayload, responsePayload) => {
  const responseList = extractArray(responsePayload);
  if (responseList.length > 0) {
    return sortCommentsOldestFirst(responseList.map(normalizeSingleComment));
  }
  const fallback =
    taskPayload?.task_comments ??
    taskPayload?.comments ??
    taskPayload?.taskComments ??
    taskPayload?.comment_list ??
    [];
  return Array.isArray(fallback) ? sortCommentsOldestFirst(fallback.map(normalizeSingleComment)) : [];
};

const normalizeHistoryList = (taskPayload) => {
  const list =
    taskPayload?.history ??
    taskPayload?.histories ??
    taskPayload?.task_logs ??
    taskPayload?.status_logs ??
    taskPayload?.logs ??
    [];
  return Array.isArray(list) ? list : [];
};

const normalizeAttachmentList = (taskPayload) => {
  const list = taskPayload?.attachments ?? taskPayload?.task_attachments ?? [];
  return Array.isArray(list) ? list : [];
};

const priorityColor = (priority) => {
  const p = String(priority || '').toLowerCase();
  if (p === 'high') return 'error';
  if (p === 'medium') return 'warning';
  if (p === 'low') return 'success';
  return 'default';
};

const downloadBlob = (blob, filename) => {
  const url = window.URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename || 'download';
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  window.URL.revokeObjectURL(url);
};

const emptyCommentHtml = '';

const isEditorHtmlEmpty = (html) => {
  if (!html) return true;
  const parser = new DOMParser();
  const doc = parser.parseFromString(String(html), 'text/html');
  if (doc.body.querySelector('img,video,audio,iframe,embed,object')) {
    return false;
  }
  const normalized = (doc.body.textContent || '').replace(/\u00a0/g, ' ').trim();
  return normalized === '';
};

const sanitizeCommentHtml = (html) => {
  if (!html) return '';
  const parser = new DOMParser();
  const doc = parser.parseFromString(String(html), 'text/html');
  doc.querySelectorAll('script,style,iframe,object,embed').forEach((node) => node.remove());
  doc.querySelectorAll('*').forEach((node) => {
    [...node.attributes].forEach((attr) => {
      const name = attr.name.toLowerCase();
      const value = attr.value || '';
      if (name.startsWith('on')) node.removeAttribute(attr.name);
      if ((name === 'href' || name === 'src') && /^javascript:/i.test(value.trim())) {
        node.removeAttribute(attr.name);
      }
    });
  });
  return doc.body.innerHTML;
};

const formatCommentHtmlForDisplay = (value) => {
  if (value == null || value === '') return '—';
  const raw = String(value);
  if (/<[a-z][\s\S]*>/i.test(raw)) {
    return sanitizeCommentHtml(raw);
  }
  return raw
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/\n/g, '<br />');
};

const resolveAttachmentUrl = (path) => {
  if (!path) return '';
  if (/^https?:\/\//i.test(path)) return path;
  const base = (getApiBaseUrl() || '').replace(/\/$/, '');
  if (!base) return path;
  return path.startsWith('/') ? `${base}${path}` : `${base}/${path}`;
};

const getAttachmentPath = (attachment) =>
  attachment?.path ?? attachment?.file_path ?? attachment?.file_url ?? attachment?.url ?? '';

const normalizeAttachmentUrlForMatch = (value) => String(value || '').split('?')[0];

const attachmentTypeIcon = (attachment) => {
  const mime = String(attachment?.mime_type ?? attachment?.type ?? '').toLowerCase();
  const name = String(
    attachment?.document_name ?? attachment?.file_name ?? attachment?.title ?? ''
  ).toLowerCase();

  if (mime.startsWith('image/') || /\.(png|jpe?g|gif|webp|bmp|svg)$/i.test(name)) {
    return <ImageOutlinedIcon fontSize="small" />;
  }
  if (mime.includes('pdf') || /\.pdf$/i.test(name)) {
    return <PictureAsPdfOutlinedIcon fontSize="small" />;
  }
  if (
    mime.includes('sheet') ||
    mime.includes('excel') ||
    /\.(csv|xls|xlsx)$/i.test(name)
  ) {
    return <TableChartOutlinedIcon fontSize="small" />;
  }
  if (
    mime.includes('zip') ||
    mime.includes('rar') ||
    mime.includes('7z') ||
    /\.(zip|rar|7z|tar|gz)$/i.test(name)
  ) {
    return <ArchiveOutlinedIcon fontSize="small" />;
  }
  return <DescriptionOutlinedIcon fontSize="small" />;
};

const findAttachmentByRenderedUrl = (attachments, url) => {
  const target = normalizeAttachmentUrlForMatch(url);
  return (attachments || []).find((attachment) => {
    const path = getAttachmentPath(attachment);
    return normalizeAttachmentUrlForMatch(resolveAttachmentUrl(path)) === target;
  });
};

const toDownloadablePathFromUrl = (url) => {
  const raw = String(url || '').trim();
  if (!raw) return '';
  const base = (getApiBaseUrl() || '').replace(/\/$/, '');
  if (base && raw.startsWith(base)) {
    const trimmed = raw.slice(base.length);
    return trimmed.startsWith('/') ? trimmed : `/${trimmed}`;
  }
  return raw;
};

const extractAttachmentUploadMeta = (body) => {
  const data = body?.data ?? body ?? {};
  const inner = data?.data ?? data;
  return {
    id:
      inner?.id ??
      body?.id ??
      data?.id ??
      inner?.attachment?.id ??
      data?.attachment?.id ??
      inner?.attachment_id ??
      data?.attachment_id ??
      null,
    path:
      inner?.path ??
      data?.path ??
      body?.path ??
      inner?.file_url ??
      data?.file_url ??
      body?.file_url ??
      '',
  };
};

export default function TaskDetailsModal({ open, taskId, onClose, onUpdated }) {
  const { user } = useAuth();
  const [tab, setTab] = useState(0);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [commentSubmitting, setCommentSubmitting] = useState(false);
  const [commentUploadingImage, setCommentUploadingImage] = useState(false);
  const [downloadingId, setDownloadingId] = useState(null);

  const [task, setTask] = useState(null);
  const [comments, setComments] = useState([]);
  const [history, setHistory] = useState([]);
  const [taskAttachments, setTaskAttachments] = useState([]);

  const [statusOptions, setStatusOptions] = useState([]);
  const [userOptions, setUserOptions] = useState([]);
  const [leadOptions, setLeadOptions] = useState([]);
  const [serviceOptions, setServiceOptions] = useState([]);
  const [parentOptions, setParentOptions] = useState([]);
  const [referenceLoading, setReferenceLoading] = useState({
    statuses: false,
    users: false,
    leads: false,
    services: false,
    parents: false,
  });
  const [referenceLoaded, setReferenceLoaded] = useState({
    statuses: false,
    users: false,
    leads: false,
    services: false,
    parents: false,
  });
  const [activeQuickEdit, setActiveQuickEdit] = useState(null);
  const [quickEditDraft, setQuickEditDraft] = useState(null);

  const statusOptionsRef = useRef(statusOptions);
  const userOptionsRef = useRef(userOptions);
  const leadOptionsRef = useRef(leadOptions);
  const serviceOptionsRef = useRef(serviceOptions);
  const parentOptionsRef = useRef(parentOptions);
  const commentEditorRef = useRef(null);
  const commentSelectionRef = useRef(null);
  const commentFileInputRef = useRef(null);

  useEffect(() => {
    statusOptionsRef.current = statusOptions;
  }, [statusOptions]);

  useEffect(() => {
    userOptionsRef.current = userOptions;
  }, [userOptions]);

  useEffect(() => {
    leadOptionsRef.current = leadOptions;
  }, [leadOptions]);

  useEffect(() => {
    serviceOptionsRef.current = serviceOptions;
  }, [serviceOptions]);

  useEffect(() => {
    parentOptionsRef.current = parentOptions;
  }, [parentOptions]);

  const [selectedStatus, setSelectedStatus] = useState(null);
  const [selectedResponsible, setSelectedResponsible] = useState(null);
  const [selectedLead, setSelectedLead] = useState(null);
  const [selectedService, setSelectedService] = useState(null);
  const [selectedParent, setSelectedParent] = useState(null);
  const [selectedAssignees, setSelectedAssignees] = useState([]);
  const [selectedObservers, setSelectedObservers] = useState([]);

  const [title, setTitle] = useState('');
  const [priority, setPriority] = useState('medium');
  const [deadline, setDeadline] = useState('');
  const [description, setDescription] = useState('');
  const [saveAsTemplate, setSaveAsTemplate] = useState(false);
  const [commentText, setCommentText] = useState(emptyCommentHtml);
  const [commentAttachmentIds, setCommentAttachmentIds] = useState([]);
  const [commentAttachedFiles, setCommentAttachedFiles] = useState([]);
  const [commentDragActive, setCommentDragActive] = useState(false);
  const [activeMainEdit, setActiveMainEdit] = useState(null);
  const [titleDraft, setTitleDraft] = useState('');
  const [descriptionDraft, setDescriptionDraft] = useState('');

  const taskTitle = task?.title ?? 'Task details';

  const syncCommentEditorState = useCallback(() => {
    const html = commentEditorRef.current?.innerHTML ?? '';
    setCommentText(isEditorHtmlEmpty(html) ? emptyCommentHtml : html);
  }, []);

  const resetCommentComposer = useCallback(() => {
    setCommentText(emptyCommentHtml);
    setCommentAttachmentIds([]);
    setCommentAttachedFiles([]);
    setCommentDragActive(false);
    if (commentEditorRef.current) {
      commentEditorRef.current.innerHTML = emptyCommentHtml;
    }
    if (commentFileInputRef.current) {
      commentFileInputRef.current.value = '';
    }
  }, []);

  useEffect(() => {
    if (!commentEditorRef.current) return;
    if (commentEditorRef.current.innerHTML !== commentText) {
      commentEditorRef.current.innerHTML = commentText;
    }
  }, [commentText]);

  const focusCommentEditor = useCallback(() => {
    window.requestAnimationFrame(() => {
      commentEditorRef.current?.focus();
    });
  }, []);

  const saveCommentSelection = useCallback(() => {
    const selection = window.getSelection();
    if (!selection || selection.rangeCount === 0) return null;
    const range = selection.getRangeAt(0);
    if (!commentEditorRef.current?.contains(range.commonAncestorContainer)) return null;
    const cloned = range.cloneRange();
    commentSelectionRef.current = cloned;
    return cloned;
  }, []);

  const restoreCommentSelection = useCallback((range = commentSelectionRef.current) => {
    if (!range) return false;
    const selection = window.getSelection();
    if (!selection) return false;
    selection.removeAllRanges();
    selection.addRange(range);
    return true;
  }, []);

  const insertHtmlAtCommentSelection = useCallback(
    (html, fallbackToEnd = true) => {
      focusCommentEditor();
      window.requestAnimationFrame(() => {
        const editor = commentEditorRef.current;
        if (!editor) return;

        const selection = window.getSelection();
        if (!restoreCommentSelection() && fallbackToEnd) {
          const range = document.createRange();
          range.selectNodeContents(editor);
          range.collapse(false);
          if (selection) {
            selection.removeAllRanges();
            selection.addRange(range);
          }
        }

        document.execCommand('insertHTML', false, html);
        syncCommentEditorState();
        saveCommentSelection();
      });
    },
    [focusCommentEditor, restoreCommentSelection, saveCommentSelection, syncCommentEditorState]
  );

  const uploadCommentFile = useCallback(
    async (file) => {
      if (!taskId) throw new Error('Task is required before uploading files.');
      const fd = new FormData();
      fd.append('file', file);
      fd.append('model_id', String(taskId));
      fd.append('model_type', 'Task');

      const { data } = await api.post(ATTACHMENTS.LIST, fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      const meta = extractAttachmentUploadMeta(data);
      return {
        id: meta.id,
        name: file?.name || meta.path || 'attachment',
        mimeType: file?.type || '',
        isImage: String(file?.type || '').startsWith('image/'),
        path: meta.path,
        url: resolveAttachmentUrl(meta.path),
      };
    },
    [taskId]
  );

  const handleCommentToolbarAction = useCallback(
    async (action) => {
      focusCommentEditor();
      restoreCommentSelection();

      if (action === 'link') {
        const url = window.prompt('Enter link URL', 'https://');
        if (!url) return;
        document.execCommand('createLink', false, url);
      } else if (action === 'quote') {
        document.execCommand('formatBlock', false, 'blockquote');
      } else if (action === 'code') {
        const selection = window.getSelection();
        const selectedText = selection?.toString() || 'code';
        document.execCommand('insertHTML', false, `<code>${selectedText}</code>`);
      } else if (action === 'bullet') {
        document.execCommand('insertUnorderedList', false);
      } else if (action === 'number') {
        document.execCommand('insertOrderedList', false);
      } else {
        document.execCommand(action, false);
      }

      syncCommentEditorState();
      saveCommentSelection();
    },
    [focusCommentEditor, restoreCommentSelection, saveCommentSelection, syncCommentEditorState]
  );

  const handleCommentFileUpload = useCallback(
    async (files, { insertImages = true } = {}) => {
      const pickedFiles = Array.from(files || []).filter(Boolean);
      if (pickedFiles.length === 0) return;

      saveCommentSelection();
      setCommentUploadingImage(true);
      try {
        for (const file of pickedFiles) {
          const uploaded = await uploadCommentFile(file);
          if (!uploaded?.id && !uploaded?.url) {
            throw new Error('File uploaded but no attachment metadata was returned.');
          }

          if (uploaded.id) {
            setCommentAttachmentIds((prev) =>
              prev.includes(uploaded.id) ? prev : [...prev, uploaded.id]
            );
          }

          setCommentAttachedFiles((prev) =>
            prev.some((item) => String(item.id) === String(uploaded.id))
              ? prev
              : [
                  ...prev,
                  {
                    id: uploaded.id,
                    path: uploaded.path,
                    url: uploaded.url,
                    file_name: uploaded.name,
                    document_name: uploaded.name,
                    mime_type: uploaded.mimeType,
                    isImage: uploaded.isImage,
                  },
                ]
          );

          if (insertImages && uploaded.isImage && uploaded.url) {
            insertHtmlAtCommentSelection(
              `<img src="${uploaded.url}" alt="${uploaded.name || 'uploaded-image'}" style="max-width:100%;height:auto;border-radius:6px;" />`,
              true
            );
          }
        }
      } catch (err) {
        toast.error(err.response?.data?.message || err.message || 'Failed to upload attachment');
      } finally {
        setCommentUploadingImage(false);
      }
    },
    [insertHtmlAtCommentSelection, saveCommentSelection, uploadCommentFile]
  );

  const handleCommentAttachmentRemove = useCallback(
    (fileToRemove) => {
      if (!fileToRemove) return;
      setCommentAttachmentIds((prev) =>
        prev.filter((id) => String(id) !== String(fileToRemove.id))
      );
      setCommentAttachedFiles((prev) =>
        prev.filter((item) => String(item.id) !== String(fileToRemove.id))
      );

      if (fileToRemove.isImage && commentEditorRef.current && fileToRemove.url) {
        const targetUrl = normalizeAttachmentUrlForMatch(fileToRemove.url);
        commentEditorRef.current.querySelectorAll('img').forEach((img) => {
          if (normalizeAttachmentUrlForMatch(img.getAttribute('src')) === targetUrl) {
            img.remove();
          }
        });
        syncCommentEditorState();
      }
    },
    [syncCommentEditorState]
  );

  const handleCommentImagePaste = useCallback(
    async (event) => {
      const files = Array.from(event.clipboardData?.items || [])
        .filter((item) => item.kind === 'file')
        .map((item) => item.getAsFile())
        .filter(Boolean);
      if (files.length === 0) return;

      event.preventDefault();
      await handleCommentFileUpload(files, { insertImages: true });
    },
    [handleCommentFileUpload]
  );

  const handleCommentEditorDrop = useCallback(
    async (event) => {
      event.preventDefault();
      setCommentDragActive(false);
      const files = Array.from(event.dataTransfer?.files || []);
      if (files.length === 0) return;
      await handleCommentFileUpload(files, { insertImages: true });
    },
    [handleCommentFileUpload]
  );

  const handleCommentFileInputChange = useCallback(
    async (event) => {
      const files = Array.from(event.target.files || []);
      await handleCommentFileUpload(files, { insertImages: true });
      if (event.target) event.target.value = '';
    },
    [handleCommentFileUpload]
  );

  const commentToolbarActions = [
    { key: 'bold', icon: FormatBoldIcon },
    { key: 'italic', icon: FormatItalicIcon },
    { key: 'underline', icon: FormatUnderlinedIcon },
    { key: 'link', icon: InsertLinkIcon },
    { key: 'quote', icon: FormatQuoteIcon },
    { key: 'code', icon: CodeIcon },
    { key: 'bullet', icon: FormatListBulletedIcon },
    { key: 'number', icon: FormatListNumberedIcon },
  ];

  const setReferenceLoadingFlag = useCallback((key, value) => {
    setReferenceLoading((prev) => ({ ...prev, [key]: value }));
  }, []);

  const setReferenceLoadedFlag = useCallback((key, value) => {
    setReferenceLoaded((prev) => ({ ...prev, [key]: value }));
  }, []);

  const loadStatuses = useCallback(async () => {
    setReferenceLoadingFlag('statuses', true);
    try {
      let response;
      try {
        response = await api.get(STATUSES.BY_MODEL('task'));
      } catch {
        try {
          response = await api.get(STATUSES.BY_MODEL('Task'));
        } catch {
          response = await api.get(STATUSES.LIST, { params: { paginate: 0 } });
        }
      }
      const list = extractList(response.data);
      const options = (Array.isArray(list) ? list : []).map((s) => ({
        id: s.id,
        label: statusLabel(s),
        raw: s,
      }));
      setStatusOptions((prev) => options.reduce((acc, option) => ensureOption(acc, option), prev));
      setReferenceLoadedFlag('statuses', true);
    } finally {
      setReferenceLoadingFlag('statuses', false);
    }
  }, [setReferenceLoadedFlag, setReferenceLoadingFlag]);

  const loadUsers = useCallback(async () => {
    setReferenceLoadingFlag('users', true);
    try {
      const { data } = await api.get(USERS.LIST, { params: { paginate: 0 } });
      const list = extractList(data);
      const options = (Array.isArray(list) ? list : []).map(userOption).filter((u) => u.id);
      setUserOptions((prev) => options.reduce((acc, option) => ensureOption(acc, option), prev));
      setReferenceLoadedFlag('users', true);
    } finally {
      setReferenceLoadingFlag('users', false);
    }
  }, [setReferenceLoadedFlag, setReferenceLoadingFlag]);

  const loadLeads = useCallback(async () => {
    setReferenceLoadingFlag('leads', true);
    try {
      const { data } = await api.get(LEADS.LIST, { params: { paginate: 0 } });
      const list = extractList(data);
      const options = (Array.isArray(list) ? list : []).map(leadOption).filter((u) => u.id);
      setLeadOptions((prev) => options.reduce((acc, option) => ensureOption(acc, option), prev));
      setReferenceLoadedFlag('leads', true);
    } finally {
      setReferenceLoadingFlag('leads', false);
    }
  }, [setReferenceLoadedFlag, setReferenceLoadingFlag]);

  const loadServices = useCallback(async () => {
    setReferenceLoadingFlag('services', true);
    try {
      const { data } = await api.get(SERVICES.LIST, { params: { paginate: 0 } });
      const list = extractList(data);
      const options = (Array.isArray(list) ? list : []).map(serviceOption).filter((u) => u.id);
      setServiceOptions((prev) => options.reduce((acc, option) => ensureOption(acc, option), prev));
      setReferenceLoadedFlag('services', true);
    } finally {
      setReferenceLoadingFlag('services', false);
    }
  }, [setReferenceLoadedFlag, setReferenceLoadingFlag]);

  const loadParents = useCallback(async () => {
    setReferenceLoadingFlag('parents', true);
    try {
      const { data } = await api.get(TASKS.LIST, {
        params: { paginate: 0, ...buildTaskRelationParams() },
      });
      const list = extractList(data);
      const options = (Array.isArray(list) ? list : [])
        .filter((item) => item?.id && item.id !== taskId)
        .map(taskOption);
      setParentOptions((prev) => options.reduce((acc, option) => ensureOption(acc, option), prev));
      setReferenceLoadedFlag('parents', true);
    } finally {
      setReferenceLoadingFlag('parents', false);
    }
  }, [setReferenceLoadedFlag, setReferenceLoadingFlag, taskId]);

  const ensureReferenceData = useCallback(
    async (keys) => {
      const uniqueKeys = [...new Set(keys || [])];
      const loaders = {
        statuses: loadStatuses,
        users: loadUsers,
        leads: loadLeads,
        services: loadServices,
        parents: loadParents,
      };

      await Promise.all(
        uniqueKeys.map(async (key) => {
          if (!loaders[key] || referenceLoaded[key]) return;
          try {
            await loaders[key]();
          } catch (err) {
            toast.error(err.response?.data?.message || `Failed to load ${key}`);
          }
        })
      );
    },
    [loadLeads, loadParents, loadServices, loadStatuses, loadUsers, referenceLoaded]
  );

  const hydrateTaskState = useCallback(
    (payload) => {
      setTask(payload);
      setComments(normalizeCommentList(payload, null));
      setHistory(normalizeHistoryList(payload));
      setTaskAttachments(normalizeAttachmentList(payload));

      setTitle(payload?.title ?? '');
      setTitleDraft(payload?.title ?? '');
      setPriority(payload?.priority ?? 'medium');
      setDeadline(formatTaskDeadlineForInput(payload?.deadline));
      setDescription(payload?.description ?? '');
      setDescriptionDraft(payload?.description ?? '');
      setSaveAsTemplate(
        payload?.save_as_template === true ||
          payload?.save_as_template === 1 ||
          String(payload?.save_as_template ?? '') === '1'
      );

      const nestedStatus = payload?.status && typeof payload.status === 'object' ? payload.status : null;
      const statusId = payload?.status_id ?? nestedStatus?.id;
      if (statusId) {
        const option = nestedStatus
          ? { id: statusId, label: statusLabel(nestedStatus), raw: nestedStatus }
          : statusOptionsRef.current.find((o) => o.id === statusId) ?? {
              id: statusId,
              label: String(statusId),
              raw: {},
            };
        setSelectedStatus(option);
        setStatusOptions((prev) => ensureOption(prev, option));
      } else {
        setSelectedStatus(null);
      }

      const nestedResponsible =
        payload?.responsible && typeof payload.responsible === 'object' ? payload.responsible : null;
      const responsibleId = payload?.responsible_id ?? nestedResponsible?.id;
      if (responsibleId) {
        const option = nestedResponsible
          ? userOption(nestedResponsible)
          : userOptionsRef.current.find((o) => o.id === responsibleId) ?? {
              id: responsibleId,
              label: String(responsibleId),
              raw: {},
            };
        setSelectedResponsible(option);
        setUserOptions((prev) => ensureOption(prev, option));
      } else {
        setSelectedResponsible(null);
      }

      const nestedLead = payload?.lead && typeof payload.lead === 'object' ? payload.lead : null;
      const leadId = payload?.lead_id ?? nestedLead?.id;
      if (leadId) {
        const option = nestedLead
          ? leadOption(nestedLead)
          : leadOptionsRef.current.find((o) => o.id === leadId) ?? {
              id: leadId,
              label: String(leadId),
              raw: {},
            };
        setSelectedLead(option);
        setLeadOptions((prev) => ensureOption(prev, option));
      } else {
        setSelectedLead(null);
      }

      const nestedService = payload?.service && typeof payload.service === 'object' ? payload.service : null;
      const serviceId = payload?.service_id ?? nestedService?.id;
      if (serviceId) {
        const option = nestedService
          ? serviceOption(nestedService)
          : serviceOptionsRef.current.find((o) => o.id === serviceId) ?? {
              id: serviceId,
              label: String(serviceId),
              raw: {},
            };
        setSelectedService(option);
        setServiceOptions((prev) => ensureOption(prev, option));
      } else {
        setSelectedService(null);
      }

      const nestedParent = payload?.parent && typeof payload.parent === 'object' ? payload.parent : null;
      const parentId = payload?.parent_id ?? nestedParent?.id;
      if (parentId) {
        const option = nestedParent
          ? taskOption(nestedParent)
          : parentOptionsRef.current.find((o) => o.id === parentId) ?? {
              id: parentId,
              label: String(parentId),
              raw: {},
            };
        setSelectedParent(option);
        setParentOptions((prev) => ensureOption(prev, option));
      } else {
        setSelectedParent(null);
      }

      const assignees = Array.isArray(payload?.assignees) ? payload.assignees.map(userOption).filter((u) => u.id) : [];
      setSelectedAssignees(assignees);
      if (assignees.length) {
        setUserOptions((prev) => assignees.reduce((acc, option) => ensureOption(acc, option), prev));
      }

      const observers = Array.isArray(payload?.observers) ? payload.observers.map(userOption).filter((u) => u.id) : [];
      setSelectedObservers(observers);
      if (observers.length) {
        setUserOptions((prev) => observers.reduce((acc, option) => ensureOption(acc, option), prev));
      }
    },
    []
  );

  const fetchTaskDetails = useCallback(async () => {
    if (!taskId) return;
    const { data } = await api.get(TASKS.BY_ID(taskId));
    const payload = data?.data ?? data;
    hydrateTaskState(payload);
    return payload;
  }, [hydrateTaskState, taskId]);

  const handleRefreshTask = useCallback(async () => {
    if (!taskId) return;
    setRefreshing(true);
    try {
      await fetchTaskDetails();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to refresh task details');
    } finally {
      setRefreshing(false);
    }
  }, [fetchTaskDetails, taskId]);

  useEffect(() => {
    if (!taskId) return;
    setParentOptions((prev) => prev.filter((item) => item?.id !== taskId));
  }, [taskId]);

  useEffect(() => {
    if (!open || !taskId) return;
    let cancelled = false;

    const fetchAll = async () => {
      setTab(0);
      resetCommentComposer();
      setActiveMainEdit(null);
      setActiveQuickEdit(null);
      setQuickEditDraft(null);
      setComments([]);
      setLoading(true);
      try {
        const [taskRes] = await Promise.allSettled([api.get(TASKS.BY_ID(taskId))]);

        if (cancelled) return;

        if (taskRes.status === 'fulfilled') {
          const payload = taskRes.value.data?.data ?? taskRes.value.data;
          hydrateTaskState(payload);
        } else {
          toast.error(taskRes.reason?.response?.data?.message || 'Failed to load task details');
          onClose();
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    fetchAll();

    return () => {
      cancelled = true;
    };
  }, [hydrateTaskState, onClose, open, taskId]);

  const buildTaskFormData = (values) => {
    const fd = new FormData();
    fd.append('status_id', String(values.selectedStatus?.id ?? ''));
    fd.append('responsible_id', String(values.selectedResponsible?.id ?? ''));
    fd.append('lead_id', String(values.selectedLead?.id ?? ''));
    fd.append('service_id', String(values.selectedService?.id ?? ''));
    if (values.selectedParent?.id) {
      fd.append('parent_id', String(values.selectedParent.id));
    }
    fd.append('title', values.title ?? '');
    fd.append('priority', values.priority ?? '');
    if (values.deadline) fd.append('deadline', formatTaskDeadlineForApi(values.deadline));
    fd.append('description', values.description ?? '');
    fd.append('save_as_template', values.saveAsTemplate ? '1' : '0');
    values.selectedAssignees.forEach((item, idx) => {
      fd.append(`assignees[${idx}][id]`, String(item.id));
      fd.append(`assignees[${idx}][role]`, 'assignee');
    });
    values.selectedObservers.forEach((item, idx) => {
      fd.append(`observers[${idx}][id]`, String(item.id));
      fd.append(`observers[${idx}][role]`, 'observer');
    });
    return fd;
  };

  const handleSaveTask = async (overrides = {}) => {
    const values = {
      selectedStatus,
      selectedResponsible,
      selectedLead,
      selectedService,
      selectedParent,
      selectedAssignees,
      selectedObservers,
      title,
      priority,
      deadline,
      description,
      saveAsTemplate,
      ...overrides,
    };

    if (!values.selectedStatus?.id || !values.selectedResponsible?.id || !values.selectedLead?.id || !values.selectedService?.id) {
      toast.warn('Select status, responsible, lead, and service');
      return false;
    }
    if (!String(values.title || '').trim()) {
      toast.warn('Enter a task title');
      return false;
    }
    if (!values.deadline) {
      toast.warn('Select a deadline');
      return false;
    }

    setSaving(true);
    try {
      const fd = buildTaskFormData(values);
      const q = new URLSearchParams(buildTaskRelationParams()).toString();
      await api.put(`${TASKS.BY_ID(taskId)}?${q}`, fd);
      toast.success('Task updated');
      await fetchTaskDetails();
      onUpdated?.();
      return true;
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to update task');
      return false;
    } finally {
      setSaving(false);
    }
  };

  const handleCommentSubmit = async () => {
    const sanitizedComment = sanitizeCommentHtml(commentText);
    if (isEditorHtmlEmpty(sanitizedComment) && commentAttachmentIds.length === 0) {
      toast.warn('Enter a comment');
      return;
    }
    if (!taskId || !user?.id) {
      toast.error('Could not identify task or author');
      return;
    }
    setCommentSubmitting(true);
    try {
      const fd = new FormData();
      fd.append('task_id', String(taskId));
      fd.append('author_id', String(user.id));
      fd.append('comment', sanitizedComment);
      commentAttachmentIds.forEach((attachmentId, idx) => {
        fd.append(`attachments[${idx}]`, String(attachmentId));
      });
      await api.post(TASK_COMMENTS.LIST, fd);
      toast.success('Comment added');
      resetCommentComposer();
      await fetchTaskDetails();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to add comment');
    } finally {
      setCommentSubmitting(false);
    }
  };

  const handleDownloadAttachment = async (attachment) => {
    const attachmentPath = getAttachmentPath(attachment);
    const fileName =
      attachment?.document_name ?? attachment?.file_name ?? attachment?.title ?? 'attachment';
    try {
      setDownloadingId(
        attachment?.id ?? attachment?.attachment_id ?? attachmentPath ?? fileName
      );
      if (!attachmentPath) {
        throw new Error('No attachment source available');
      }
      const response = await api.post(
        ATTACHMENTS.DOWNLOAD_BY_PATH,
        { path: attachmentPath },
        { responseType: 'blob' }
      );
      downloadBlob(response.data, fileName);
    } catch (err) {
      toast.error(err.response?.data?.message || err.message || 'Failed to download attachment');
    } finally {
      setDownloadingId(null);
    }
  };

  const handleCommentContentClick = useCallback(
    async (comment, event) => {
      const imageEl = event.target?.closest?.('img');
      if (!imageEl) return;

      event.preventDefault();
      const renderedSrc = imageEl.getAttribute('src') || '';
      const matchingAttachment = findAttachmentByRenderedUrl(comment?.attachments, renderedSrc);

      if (matchingAttachment) {
        await handleDownloadAttachment(matchingAttachment);
        return;
      }

      const fallbackPath = toDownloadablePathFromUrl(renderedSrc);
      if (!fallbackPath) return;

      await handleDownloadAttachment({
        path: fallbackPath,
        file_name: imageEl.getAttribute('alt') || 'image',
      });
    },
    [handleDownloadAttachment]
  );

  const quickEditRequirements = {
    status: ['statuses'],
    priority: [],
    responsible: ['users'],
    deadline: [],
    lead: ['leads'],
    service: ['services'],
    parent: ['parents'],
    participants: ['users'],
    observers: ['users'],
    template: [],
  };

  const getQuickEditInitialValue = useCallback(
    (field) => {
      switch (field) {
        case 'status':
          return selectedStatus;
        case 'priority':
          return priority;
        case 'responsible':
          return selectedResponsible;
        case 'deadline':
          return deadline;
        case 'lead':
          return selectedLead;
        case 'service':
          return selectedService;
        case 'parent':
          return selectedParent;
        case 'participants':
          return selectedAssignees;
        case 'observers':
          return selectedObservers;
        case 'template':
          return saveAsTemplate;
        default:
          return null;
      }
    },
    [
      deadline,
      priority,
      saveAsTemplate,
      selectedAssignees,
      selectedLead,
      selectedObservers,
      selectedParent,
      selectedResponsible,
      selectedService,
      selectedStatus,
    ]
  );

  const openQuickEdit = useCallback(
    async (field) => {
      setActiveQuickEdit(field);
      setQuickEditDraft(getQuickEditInitialValue(field));
      await ensureReferenceData(quickEditRequirements[field] || []);
    },
    [ensureReferenceData, getQuickEditInitialValue]
  );

  const closeQuickEdit = useCallback(() => {
    setActiveQuickEdit(null);
    setQuickEditDraft(null);
  }, []);

  const buildQuickEditOverrides = useCallback(() => {
    switch (activeQuickEdit) {
      case 'status':
        return { selectedStatus: quickEditDraft };
      case 'priority':
        return { priority: quickEditDraft };
      case 'responsible':
        return { selectedResponsible: quickEditDraft };
      case 'deadline':
        return { deadline: quickEditDraft };
      case 'lead':
        return { selectedLead: quickEditDraft };
      case 'service':
        return { selectedService: quickEditDraft };
      case 'parent':
        return { selectedParent: quickEditDraft };
      case 'participants':
        return { selectedAssignees: Array.isArray(quickEditDraft) ? quickEditDraft : [] };
      case 'observers':
        return { selectedObservers: Array.isArray(quickEditDraft) ? quickEditDraft : [] };
      case 'template':
        return { saveAsTemplate: Boolean(quickEditDraft) };
      default:
        return {};
    }
  }, [activeQuickEdit, quickEditDraft]);

  const handleQuickEditSave = useCallback(async () => {
    const ok = await handleSaveTask(buildQuickEditOverrides());
    if (ok) closeQuickEdit();
  }, [buildQuickEditOverrides, closeQuickEdit, handleSaveTask]);

  const openMainEdit = useCallback(
    (field) => {
      if (field === 'title') setTitleDraft(title);
      if (field === 'description') setDescriptionDraft(description);
      setActiveMainEdit(field);
    },
    [description, title]
  );

  const closeMainEdit = useCallback(() => {
    setActiveMainEdit(null);
    setTitleDraft(title);
    setDescriptionDraft(description);
  }, [description, title]);

  const handleMainEditSave = useCallback(async () => {
    if (activeMainEdit === 'title') {
      const ok = await handleSaveTask({ title: titleDraft });
      if (ok) setActiveMainEdit(null);
      return;
    }
    if (activeMainEdit === 'description') {
      const ok = await handleSaveTask({ description: descriptionDraft });
      if (ok) setActiveMainEdit(null);
    }
  }, [activeMainEdit, descriptionDraft, handleSaveTask, titleDraft]);

  const commentCount = comments.length;
  const historyCount = history.length;
  const attachmentCount = taskAttachments.length;
  const participantNames = selectedAssignees.map((item) => item?.label).filter(Boolean).join(', ');
  const observerNames = selectedObservers.map((item) => item?.label).filter(Boolean).join(', ');
  const projectName = selectedLead?.label || task?.lead?.name || task?.lead?.dba_name || '—';
  const serviceName = selectedService?.label || task?.service?.name || '—';
  const parentName = selectedParent?.label || task?.parent?.title || '—';
  const quickEditLoading = activeQuickEdit
    ? (quickEditRequirements[activeQuickEdit] || []).some((key) => referenceLoading[key])
    : false;

  const summaryRows = useMemo(
    () => [
      { key: 'status', label: 'Status', value: selectedStatus?.label || task?.status?.name || '—', editable: true },
      { key: 'lead', label: 'Lead Name', value: projectName, editable: true },
      {
        key: 'deadline',
        label: 'Deadline',
        value: deadline ? deadline.replace('T', ' ') : task?.deadline ? String(task.deadline).replace('T', ' ').slice(0, 16) : '—',
        editable: true,
      },
      { key: 'created_by', label: 'Created by', value: task?.author?.name ?? task?.author?.username ?? '—', editable: false },
      {
        key: 'created_on',
        label: 'Created on',
        value: task?.created_at ?? '—',
        editable: false,
      },
      { key: 'priority', label: 'Priority', value: priority || '—', editable: true, chip: true },
      { key: 'responsible', label: 'Responsible person', value: selectedResponsible?.label || '—', editable: true },
      { key: 'service', label: 'Service', value: serviceName, editable: true },
      { key: 'parent', label: 'Parent task', value: parentName, editable: true },
      { key: 'participants', label: 'Participants', value: participantNames || '—', editable: true },
      { key: 'observers', label: 'Observers', value: observerNames || '—', editable: true },
      { key: 'template', label: 'Save as template', value: saveAsTemplate ? 'Yes' : 'No', editable: true },
    ],
    [
      deadline,
      observerNames,
      parentName,
      participantNames,
      priority,
      projectName,
      saveAsTemplate,
      selectedResponsible,
      selectedStatus,
      serviceName,
      task,
    ]
  );
  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth="xl"
      PaperProps={{
        sx: {
          borderRadius: 2,
          minHeight: '82vh',
          bgcolor: '#eef2f6',
        },
      }}
    >
      <TaskDetailsHeader
        taskTitle={taskTitle}
        statusText={task?.status?.name ?? 'Task details'}
        loading={loading}
        refreshing={refreshing}
        saving={saving}
        onRefresh={handleRefreshTask}
        onClose={onClose}
        onSave={handleSaveTask}
      />
      <DialogContent dividers sx={{ minHeight: 520, p: 2.5 }}>
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 360 }}>
            <CircularProgress />
          </Box>
        ) : (
          <Grid container spacing={2.5}>
            <Grid item xs={12} md={8}>
              <EditableTaskTextCard
                label="Task title"
                value={title}
                active={activeMainEdit === 'title'}
                draft={titleDraft}
                onDraftChange={setTitleDraft}
                onEdit={() => openMainEdit('title')}
                onCancel={closeMainEdit}
                onSave={handleMainEditSave}
                saving={saving}
                valueTypographyProps={{ variant: 'h5', sx: { fontWeight: 700, mt: 0.5, wordBreak: 'break-word' } }}
                editorInputSx={{ fontSize: 24, fontWeight: 600 }}
              />

              <EditableTaskTextCard
                label="Description"
                value={description}
                active={activeMainEdit === 'description'}
                draft={descriptionDraft}
                onDraftChange={setDescriptionDraft}
                onEdit={() => openMainEdit('description')}
                onCancel={closeMainEdit}
                onSave={handleMainEditSave}
                saving={saving}
                multiline
                minRows={2}
                valueTypographyProps={{ variant: 'body1' }}
              />

              <TaskActivityTabsCard
                tab={tab}
                onTabChange={setTab}
                commentCount={commentCount}
                historyCount={historyCount}
                attachmentCount={attachmentCount}
                commentsProps={{
                  comments,
                  currentUserId: user?.id,
                  isEditorHtmlEmpty,
                  formatCommentHtmlForDisplay,
                  handleCommentContentClick,
                  attachmentTypeIcon,
                  handleDownloadAttachment,
                  commentFileInputRef,
                  handleCommentFileInputChange,
                  commentDragActive,
                  setCommentDragActive,
                  handleCommentEditorDrop,
                  commentToolbarActions,
                  handleCommentToolbarAction,
                  commentEditorRef,
                  syncCommentEditorState,
                  handleCommentImagePaste,
                  saveCommentSelection,
                  commentAttachedFiles,
                  handleCommentAttachmentRemove,
                  commentUploadingImage,
                  commentSubmitting,
                  handleCommentSubmit,
                }}
                history={history}
                attachmentsProps={{
                  taskAttachments,
                  handleDownloadAttachment,
                  downloadingId,
                }}
              />
            </Grid>

            <Grid item xs={12} md={4}>
              <TaskSummaryPanel
                taskTitle={task?.title ?? 'Task'}
                summaryRows={summaryRows}
                activeQuickEdit={activeQuickEdit}
                openQuickEdit={openQuickEdit}
                quickEditLoading={quickEditLoading}
                quickEditDraft={quickEditDraft}
                setQuickEditDraft={setQuickEditDraft}
                statusOptions={statusOptions}
                userOptions={userOptions}
                leadOptions={leadOptions}
                serviceOptions={serviceOptions}
                parentOptions={parentOptions}
                priorityOptions={PRIORITY_OPTIONS}
                closeQuickEdit={closeQuickEdit}
                handleQuickEditSave={handleQuickEditSave}
                saving={saving}
                priorityColor={priorityColor}
              />
            </Grid>
          </Grid>
        )}
      </DialogContent>
    </Dialog>
  );
}
